import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Wallet, Calendar, CheckCircle2, XCircle, Clock, FileText,
  Upload, Send, LogOut, ArrowLeft,
} from 'lucide-react';
import { contractorLabsAPI, publicAPI } from '../../services/api';
import { formatDate, formatDateShort } from '../../utils/date';

const SAR = (n) => `${Number(n || 0).toFixed(2)} ر.س`;

const StatusBadge = ({ status }) => {
  const map = {
    pending:   { label: 'قيد المراجعة', color: 'bg-amber-50 text-amber-700 border-amber-200',   Icon: Clock },
    confirmed: { label: 'تم التأكيد',    color: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
    rejected:  { label: 'مرفوض',         color: 'bg-red-50 text-red-700 border-red-200',           Icon: XCircle },
  };
  const m = map[status] || map.pending;
  const { Icon } = m;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${m.color}`}>
      <Icon className="w-3.5 h-3.5" />{m.label}
    </span>
  );
};

const ContractorStatement = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantBranding, setTenantBranding] = useState(null);
  const tenantLogo = tenantBranding?.branding?.logoUrl || '/listech-logo.svg';
  const tenantName = tenantBranding?.nameAr || tenantBranding?.name || 'LIS Techs';

  // New payment form
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    amount: '', transfer_date: new Date().toISOString().slice(0, 10),
    reference_number: '', notes: '',
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('contractorToken');
    const u = localStorage.getItem('contractorUser');
    if (!token) return navigate('/contractor/login');
    if (u) {
      const parsed = JSON.parse(u);
      setUser(parsed);
      if (parsed.email) {
        publicAPI.getContractorBranding(parsed.email)
          .then(({ data }) => { if (data?.data?.branding) setTenantBranding(data.data); })
          .catch(() => {});
      }
    }
    fetchStatement();
  }, [navigate]);

  const fetchStatement = async () => {
    setLoading(true);
    try {
      const { data } = await contractorLabsAPI.getMyStatement();
      setStatement(data?.data);
    } catch (e) {
      toast.error('تعذّر تحميل كشف الحساب');
    }
    setLoading(false);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error('المبلغ مطلوب'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('amount', form.amount);
      fd.append('transfer_date', form.transfer_date);
      if (form.reference_number) fd.append('reference_number', form.reference_number);
      if (form.notes) fd.append('notes', form.notes);
      if (file) fd.append('receipt', file);
      await contractorLabsAPI.submitMyPayment(fd);
      toast.success('تم إرسال إشعار التحويل — بانتظار تأكيد الإدارة');
      setShowForm(false);
      setForm({ amount: '', transfer_date: new Date().toISOString().slice(0, 10), reference_number: '', notes: '' });
      setFile(null);
      fetchStatement();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الإرسال');
    }
    setSubmitting(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('contractorToken');
    localStorage.removeItem('contractorUser');
    navigate('/contractor/login');
  };

  if (loading || !statement) {
    return <div className="min-h-screen flex items-center justify-center" dir="rtl">جاري التحميل...</div>;
  }

  const { all_time, bookings, payments } = statement;

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl" style={{ fontFamily: "'Readex Pro', sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={tenantLogo} alt={tenantName} className="h-10 w-auto object-contain flex-shrink-0" />
            <div className="border-r border-gray-200 pr-3 min-w-0">
              <h1 className="text-base font-bold text-slate-800 truncate">{tenantName}</h1>
              <p className="text-blue-600 text-xs">كشف الحساب</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/contractor/dashboard')}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-slate-700 hover:bg-gray-50">
              <ArrowLeft className="w-4 h-4" /> الحجوزات
            </button>
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50">
              <LogOut className="w-4 h-4" /> خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-5">
        {/* Totals cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-slate-500 text-sm mb-1">إجمالي المطلوب</p>
            <p className="text-2xl font-bold text-slate-800">{SAR(all_time.billed)}</p>
            <p className="text-xs text-slate-400 mt-1">إجمالي الحجوزات غير الملغاة</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-slate-500 text-sm mb-1">المدفوع (مؤكد)</p>
            <p className="text-2xl font-bold text-emerald-600">{SAR(all_time.paid)}</p>
            <p className="text-xs text-slate-400 mt-1">الدفعات التي أكّدتها الإدارة</p>
          </div>
          <div className={`rounded-xl p-5 border ${all_time.outstanding > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
            <p className="text-slate-500 text-sm mb-1">الرصيد المستحق</p>
            <p className={`text-2xl font-bold ${all_time.outstanding > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
              {SAR(all_time.outstanding)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {all_time.outstanding > 0 ? 'يُرجى السداد' : 'لا يوجد مستحقات حالياً'}
            </p>
          </div>
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-800">إشعارات التحويل</h2>
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
            <Send className="w-4 h-4" /> إشعار تحويل جديد
          </button>
        </div>

        {/* Payments list */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-gray-50 text-xs font-semibold text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-right">المبلغ</th>
                  <th className="px-4 py-3 text-right">تاريخ التحويل</th>
                  <th className="px-4 py-3 text-right">رقم الحوالة</th>
                  <th className="px-4 py-3 text-right">الحالة</th>
                  <th className="px-4 py-3 text-right">ملاحظات الإدارة</th>
                </tr>
              </thead>
              <tbody>
                {payments?.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-slate-400 py-8">لا توجد إشعارات تحويل بعد</td></tr>
                ) : payments.map((p) => (
                  <tr key={p._id} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-bold text-slate-800">{SAR(p.amount)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDateShort(p.transfer_date)}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{p.reference_number || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-slate-600 text-sm">{p.admin_notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bookings list (for the period) */}
        <h2 className="text-lg font-bold text-slate-800 mb-3">سجل الحجوزات</h2>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-gray-50 text-xs font-semibold text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-right">رقم الحجز</th>
                  <th className="px-4 py-3 text-right">التاريخ</th>
                  <th className="px-4 py-3 text-right">اسم المريض</th>
                  <th className="px-4 py-3 text-right">المبلغ</th>
                </tr>
              </thead>
              <tbody>
                {bookings?.length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-slate-400 py-8">لا توجد حجوزات</td></tr>
                ) : bookings.map((b) => (
                  <tr key={b._id} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-mono text-sm font-bold text-slate-800">{b.booking_no}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDateShort(b.created_at)}</td>
                    <td className="px-4 py-3 text-slate-700">{b.patient_name}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{SAR(b.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Payment submission modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl w-[95vw] max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-slate-800">إشعار تحويل جديد</h3>
              <p className="text-sm text-slate-500">أرسل تفاصيل التحويل والإيصال، ستتم مراجعته من الإدارة.</p>
            </div>
            <form onSubmit={handleSubmitPayment} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">المبلغ (ر.س) *</label>
                <input type="number" step="0.01" min="0.01" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">تاريخ التحويل *</label>
                <input type="date" value={form.transfer_date}
                  onChange={(e) => setForm({ ...form, transfer_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">رقم الحوالة (اختياري)</label>
                <input type="text" value={form.reference_number}
                  onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">ملاحظات (اختياري)</label>
                <textarea value={form.notes} rows={2}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">إيصال التحويل (PDF/JPG/PNG ≤ 5MB)</label>
                <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full text-sm" />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-lg font-medium text-slate-700 hover:bg-gray-50">
                  إلغاء
                </button>
                <button type="submit" disabled={submitting}
                  className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {submitting ? 'جاري الإرسال...' : (<><Upload className="w-4 h-4" />إرسال</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorStatement;
